/**
 * SponsoredListingsSection — renders sponsored search hits per spec §23 / §28.4.
 *
 * Suppression rules (§23.3):
 *   - User opted out of behavioralAdvertising → section hidden entirely.
 *   - Stale listings → excluded from this section.
 *   - Out-of-stock listings → excluded from this section.
 *
 * Cap: at most MAX_SPONSORED slots are shown regardless of how many the API
 * returns. The backend controls selection; the frontend enforces the display
 * cap so the sponsored block never dominates the result page.
 *
 * Labeling (§28.4, §16 WCAG 2.2 AA):
 *   - Visible "Sponsored" heading (aria role heading).
 *   - <section> is aria-labelledby that heading so AT announces "Sponsored,
 *     region" when the user navigates into it.
 *   - Each card already carries aria-label="Sponsored" chip (ListingCard).
 *   - Empty slot is kept in the DOM as an empty <section aria-label="Sponsored
 *     listings"> so screen-reader virtual-cursor navigation is stable.
 *
 * @module SponsoredListingsSection
 */

import { Box, Typography } from '@mui/material';

import { useConsentStore } from '../../stores/consentStore';
import { ListingCard } from './ListingCard';
import type { SearchHit } from '../../services/searchApi';

/** Maximum number of sponsored hits to display in one page. */
export const MAX_SPONSORED = 3;

export interface SponsoredListingsSectionProps {
  /** All hits returned by the search API (sponsored + organic). */
  hits: SearchHit[];
}

/**
 * Filter and cap the raw hit list down to eligible sponsored entries.
 *
 * Suppression order:
 *   1. Keep only hits where `isSponsored === true`.
 *   2. Drop stale listings (dataFreshness === 'stale' | 'unavailable').
 *   3. Drop out-of-stock listings (stockStatus === 'out_of_stock').
 *   4. Enforce the MAX_SPONSORED display cap.
 */
function eligibleSponsored(hits: SearchHit[]): SearchHit[] {
  return hits
    .filter((h) => h.isSponsored)
    .filter((h) => h.dataFreshness !== 'stale' && h.dataFreshness !== 'unavailable')
    .filter((h) => h.stockStatus !== 'out_of_stock')
    .slice(0, MAX_SPONSORED);
}

/**
 * Sponsored listings section for the search results page.
 *
 * Usage:
 * ```tsx
 * <SponsoredListingsSection hits={searchQuery.data?.results ?? []} />
 * ```
 */
export function SponsoredListingsSection({ hits }: SponsoredListingsSectionProps) {
  const behavioralAdvertising = useConsentStore((s) => s.behavioralAdvertising);

  // §23.3 / §11.2 — hide sponsored when user opted out of behavioral targeting.
  if (!behavioralAdvertising) {
    return (
      <Box
        component="section"
        aria-label="Sponsored listings"
        aria-live="polite"
        data-testid="sponsored-slot"
        sx={{ minHeight: 0 }}
      />
    );
  }

  const visible = eligibleSponsored(hits);

  if (visible.length === 0) {
    return (
      <Box
        component="section"
        aria-label="Sponsored listings"
        aria-live="polite"
        data-testid="sponsored-slot"
        sx={{ minHeight: 0 }}
      />
    );
  }

  return (
    <Box
      component="section"
      aria-labelledby="sponsored-heading"
      aria-live="polite"
      data-testid="sponsored-slot"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        id="sponsored-heading"
        variant="overline"
        component="h2"
        sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
      >
        Sponsored
      </Typography>
      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          p: 0,
          m: 0,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
          },
          gap: 2,
        }}
      >
        {visible.map((hit) => (
          <Box component="li" key={hit.listingId} sx={{ display: 'flex' }}>
            <Box sx={{ width: '100%' }}>
              <ListingCard hit={hit} variant="full" />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default SponsoredListingsSection;
